import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { 
  SupportedLanguage, 
  LanguageResource, 
  I18nContextType, 
  TranslationHelper,
  LanguageDetector,
  TranslationCache,
  LANGUAGE_CONFIG
} from '../i18n';
import { TranslationService } from '../services/translationService';
import { useAuth } from './AuthContext';

interface I18nProviderProps {
  children: ReactNode;
}

// Create the context here to avoid circular dependencies
export const I18nContext = createContext<(I18nContextType & { reloadTranslations: () => Promise<void> }) | null>(null);

export const I18nProvider: React.FC<I18nProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [language, setLanguage] = useState<SupportedLanguage>(LanguageDetector.getPreferredLanguage());
  const [translations, setTranslations] = useState<LanguageResource>({});
  const [isLoading, setIsLoading] = useState(false);
  const [profileLanguageLoaded, setProfileLanguageLoaded] = useState(false);

  // Translation function with optional defaultText parameter
  const t = React.useCallback((key: string, params?: Record<string, string | number>, defaultText?: string): string => {
    // Try to get translation using the existing TranslationHelper
    const keys = key.split('.');
    let value: any = translations;
    
    // Navigate through the nested object
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Translation not found
        value = undefined;
        break;
      }
    }
    
    // If translation found and is a string, use it
    if (typeof value === 'string') {
      // Replace placeholders if params provided
      if (params) {
        return TranslationHelper.replacePlaceholders(value, params);
      }
      return value;
    }
    
    // If not found, use defaultText if provided, otherwise return key
    if (!value) {
      console.warn(`Translation missing for key: ${key} (language: ${language})${defaultText ? ' - using default text' : ''}`);
      
      if (defaultText) {
        // Replace placeholders in default text if params provided
        if (params) {
          return TranslationHelper.replacePlaceholders(defaultText, params);
        }
        return defaultText;
      }
      
      // Return key as last resort (existing behavior)
      return key;
    }
    
    // If value is not a string (shouldn't happen), return defaultText or key
    console.warn(`Translation is not a string for key: ${key} (language: ${language})`);
    return defaultText || key;
  }, [language, translations]);

  // Load user's language preference from profile when they log in
  useEffect(() => {
    const loadUserLanguage = async () => {
      if (isAuthenticated && user && !profileLanguageLoaded) {
        try {
          // Try to get user's language preference from profile
          const userProfileService = (await import('../services/userProfileService')).userProfileService;
          const profile = await userProfileService.getCurrentUserProfile();
          
          if (profile?.langID) {
            const userLang = profile.langID.toLowerCase() as SupportedLanguage;
            if (userLang === 'nb' || userLang === 'sv' || userLang === 'en') {
              setLanguage(userLang);
              LanguageDetector.saveLanguagePreference(userLang);
            }
          }
        } catch (error) {
          console.warn('Could not load user language preference:', error);
        } finally {
          setProfileLanguageLoaded(true);
        }
      }
    };

    loadUserLanguage();
  }, [isAuthenticated, user, profileLanguageLoaded]);

  // Load translations when language changes or user logs in
  useEffect(() => {
    const loadTranslations = async () => {
      if (!isAuthenticated || !user?.companyNo) {
        // Load default translations only
        try {
          setIsLoading(true);
          const defaultTranslations = await TranslationService.loadDefaultTranslations(language);
          setTranslations(defaultTranslations);
        } catch (error) {
          console.error('Failed to load default translations:', error);
        } finally {
          setIsLoading(false);
        }
        return;
      }

      try {
        setIsLoading(true);
        const fullTranslations = await TranslationService.loadTranslations(
          user.companyNo, 
          language, 
          true // Use cache
        );
        setTranslations(fullTranslations);
      } catch (error) {
        console.error('Failed to load translations:', error);
        // Fallback to default translations
        try {
          const defaultTranslations = await TranslationService.loadDefaultTranslations(language);
          setTranslations(defaultTranslations);
        } catch (defaultError) {
          console.error('Failed to load default translations:', defaultError);
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadTranslations();
  }, [language, isAuthenticated, user?.companyNo]);

  // Reset profile language loaded flag when user logs out
  useEffect(() => {
    if (!isAuthenticated) {
      setProfileLanguageLoaded(false);
    }
  }, [isAuthenticated]);

  // Handle language change
  const handleLanguageChange = React.useCallback((newLanguage: SupportedLanguage) => {
    setLanguage(newLanguage);
    LanguageDetector.saveLanguagePreference(newLanguage);
  }, []);

  // Force reload translations (useful after saving custom translations)
  const reloadTranslations = React.useCallback(async () => {
    if (!isAuthenticated || !user?.companyNo) return;

    // Clear cache first
    TranslationCache.clearCache();

    try {
      setIsLoading(true);
      const fullTranslations = await TranslationService.loadTranslations(
        user.companyNo, 
        language, 
        false // Don't use cache
      );
      setTranslations(fullTranslations);
    } catch (error) {
      console.error('Failed to reload translations:', error);
    } finally {
      setIsLoading(false);
    }
  }, [language, isAuthenticated, user?.companyNo]);

  const contextValue: I18nContextType & { reloadTranslations: () => Promise<void> } = {
    language,
    setLanguage: handleLanguageChange,
    t,
    isLoading,
    reloadTranslations
  };

  return (
    <I18nContext.Provider value={contextValue}>
      {children}
    </I18nContext.Provider>
  );
};

// Enhanced hook that includes reload function
export const useTranslation = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useTranslation must be used within I18nProvider');
  }
  return context;
};

// Language switcher component
export const LanguageSwitcher: React.FC<{ className?: string }> = ({ className = '' }) => {
  const { language, setLanguage, isLoading } = useTranslation();

  return (
    <div className={`flex space-x-2 ${className}`}>
      {LANGUAGE_CONFIG.supportedLanguages.map((lang) => (
        <button
          key={lang}
          onClick={() => setLanguage(lang)}
          disabled={isLoading}
          className={`px-3 py-1 text-sm rounded transition-colors ${
            language === lang
              ? 'bg-blue-500 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {lang.toUpperCase()}
        </button>
      ))}
    </div>
  );
};